import { useCallback, useEffect, useState } from "react";
import { useCreateFencyConversationMutation } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useApiError } from "../errors/useApiError";

const conversationPromises = new Map<string, Promise<{ id: string }>>();

const makeConversationKey = (orgId: string, personId: string) => `${orgId}:${personId}`;

const getOrCreateConversation = (
  key: string,
  create: () => Promise<{ id: string }>
): Promise<{ id: string }> => {
  const existing = conversationPromises.get(key);
  if (existing) return existing;

  const pending = create().catch((err) => {
    conversationPromises.delete(key);
    throw err;
  });
  conversationPromises.set(key, pending);
  return pending;
};

export const useExploreConversation = () => {
  const authState = useLoggedInWithOrgContextState();
  const [createConversation] = useCreateFencyConversationMutation();
  const { triggerSoft } = useApiError();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationKey, setConversationKey] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const currentKey = makeConversationKey(authState.selectedOrg.id, authState.me.id);

  useEffect(() => {
    if (conversationKey !== null && conversationKey !== currentKey) {
      conversationPromises.delete(conversationKey);
      setConversationId(null);
      setConversationKey(null);
    }
  }, [currentKey, conversationKey]);

  const invalidateConversation = useCallback(() => {
    const keyToClear = conversationKey ?? currentKey;
    conversationPromises.delete(keyToClear);
    setConversationId(null);
    setConversationKey(null);
  }, [conversationKey, currentKey]);

  const ensureConversation = useCallback(async (): Promise<string> => {
    if (conversationId && conversationKey === currentKey) {
      return conversationId;
    }

    if (conversationKey !== null && conversationKey !== currentKey) {
      conversationPromises.delete(conversationKey);
      setConversationId(null);
      setConversationKey(null);
    }

    setIsCreating(true);

    try {
      const result = await getOrCreateConversation(currentKey, () =>
        createConversation({
          organizationId: authState.selectedOrg.id,
          personId: authState.me.id,
        }).unwrap()
      );
      setConversationId(result.id);
      setConversationKey(currentKey);
      return result.id;
    } catch (err) {
      triggerSoft(err);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [
    authState.me.id,
    authState.selectedOrg.id,
    conversationId,
    conversationKey,
    createConversation,
    currentKey,
    triggerSoft,
  ]);

  return {
    conversationId,
    ensureConversation,
    invalidateConversation,
    isCreating,
  };
};
